import { useLayoutEffect } from 'react';

/**
 * Preline's behaviour layer, re-attached after every render.
 *
 * Preline is the template's JavaScript: dropdowns, overlays and collapses are declared in markup
 * (`hs-dropdown`, `data-hs-overlay`) and wired up by a script that walks the DOM and attaches
 * listeners. It does that walk once, on `DOMContentLoaded`.
 *
 * That is the whole problem. Everything interesting in this app is rendered *after* that moment —
 * the sidebar only exists once `/api/auth/me` has answered, and the branch switcher's menu is
 * rebuilt whenever the branch list changes. Any element Preline has not seen is inert markup: it
 * looks like a dropdown, and clicking it does nothing. The template gets away with a bare
 * `import "preline"` because its views are static; ours are not.
 *
 * `autoInit` is idempotent — it skips elements it has already claimed — so calling it after each
 * render is cheap and is the pattern Preline documents for frameworks. A layout effect rather than
 * `useEffect` so the wiring lands before the browser paints, closing the window in which a control
 * is visible but dead.
 */
export function usePreline(): void {
  useLayoutEffect(() => {
    void import('preline').then(() => {
      window.HSStaticMethods?.autoInit();
    });
  });
}

/**
 * Closes any open Preline dropdown.
 *
 * Needed because navigation here does not unmount the shell. A router would tear the open menu out
 * of the DOM on every route change; a key swap in `registry.tsx` leaves the sidebar and topbar
 * exactly where they were, so a menu opened to choose a destination is still hanging open once the
 * destination has loaded. Every menu that navigates calls this on its way out.
 */
export function closeOpenDropdowns(): void {
  for (const element of document.querySelectorAll('.hs-dropdown.open')) {
    element.classList.remove('open');
    element.querySelector('.hs-dropdown-menu')?.classList.add('hidden');
    element.querySelector('.hs-dropdown-toggle')?.setAttribute('aria-expanded', 'false');
  }
}

declare global {
  interface Window {
    /** Installed by `preline`; absent until the dynamic import above has resolved. */
    HSStaticMethods?: { autoInit: () => void };
  }
}
